
import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import { HelmetProvider } from 'react-helmet-async'
import './index.css'
import './App.css'
import './lib/debugUtils' // initialize debug logger globally
import { initializeCookieConsent } from './lib/managers/CookieManager'
import { normalizeGamePersistence } from './lib/guards/normalizePersistence'
import { modeAwareStorage } from './lib/managers/ModeAwareStorageManager'
import { toast } from '@/hooks/use-toast'
// Initialize cookie consent
try {
  initializeCookieConsent();
} catch (error) {
  console.error("Error initializing cookie consent:", error);
}

// Clear any leftover debug-date change guard and normalize persistence before app render
try { localStorage.removeItem('babblegram_debug_date_change_in_progress'); } catch {}
try {
  normalizeGamePersistence();
} catch (e) {
  console.error("Error normalizing game persistence:", e);
}

// Migrate legacy streak data to daily mode and clean up old cookies
try {
  // Force migration by setting mode to daily and triggering a cookie operation
  const originalMode = localStorage.getItem('babblegram_game_mode');
  localStorage.setItem('babblegram_game_mode', 'daily');
  
  // Trigger migration by accessing a streak cookie in daily mode
  modeAwareStorage.getStreak();
  
  // Check if migration was completed and clean up legacy cookies
  const migrationCompleted = localStorage.getItem('babblegram_daily_migration_completed');
  if (migrationCompleted) {
    console.log('[Cleanup] Removing legacy streak cookies after successful migration');
    
    const legacyKeys = [
      'babblegram_streak',
      'babblegram_max_streak', 
      'babblegram_last_win_date'
    ];
    
    legacyKeys.forEach(key => {
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
    
    console.log('[Cleanup] Legacy streak cookies removed');
  }
  
  // Restore original mode if it was different
  if (originalMode && originalMode !== 'daily') {
    localStorage.setItem('babblegram_game_mode', originalMode);
  }
} catch (error) {
  console.error('Error during legacy cookie cleanup:', error);
}

// Handle debug forcerefresh to simulate a new-day refresh for DAILY mode
try {
  const params = new URLSearchParams(window.location.search);
  if (params.get('debug') === 'forcerefresh') {
    console.log('[Debug] Forcing DAILY mode refresh for current day via ?debug=forcerefresh');
    // Clear only DAILY mode state to avoid affecting practice/custom
    modeAwareStorage.clearModeStateForMode('daily');

    // Remove the debug param to avoid loops and then reload
    // Mark success flag so we can show a toast after reload
    try { localStorage.setItem('babblegram_force_refresh_done', '1'); } catch {}
    const url = new URL(window.location.href);
    url.searchParams.delete('debug');
    window.history.replaceState({}, document.title, url.toString());

    setTimeout(() => window.location.reload(), 50);
  }
} catch (error) {
  console.error('Error handling ?debug=forcerefresh:', error);
}

// Defer toast check to run after reload and UI mount
setTimeout(() => {
  try {
    if (localStorage.getItem('babblegram_force_refresh_done') === '1') {
      localStorage.removeItem('babblegram_force_refresh_done');
      toast({ title: 'Daily refreshed', description: 'Forced refresh successful. Game reset to playing.' });
    }
  } catch (e) {
    console.error('Failed to show forcerefresh toast:', e);
  }
}, 350);

// Create error boundary wrapper
class ErrorBoundary extends React.Component<
  {children: React.ReactNode}, 
  {hasError: boolean; error: Error | null}
> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Application error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-gray-900 text-white p-4 min-h-screen flex flex-col items-center justify-center">
          <h2 className="text-xl mb-2">Something went wrong</h2>
          <p className="text-gray-400 mb-4">The application couldn't initialize properly</p>
          <pre className="bg-gray-800 p-2 rounded text-xs mb-4 max-w-full overflow-auto">
            {this.state.error?.toString()}
          </pre>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded"
          >
            Reload App
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Safely render the app
try {
  console.log("Initializing application...");
  
  // Check if root element exists
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error("Failed to find the root element");
  }
  
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <ErrorBoundary>
        <HelmetProvider>
          <App />
        </HelmetProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
  
  console.log("Application initialized successfully");
} catch (error) {
  console.error("Fatal error during app initialization:", error);
  document.body.innerHTML = `
    <div style="color: white; background: #111; padding: 20px; font-family: system-ui, sans-serif;">
      <h2>Failed to initialize the application</h2>
      <p>Please refresh the page or try again later.</p>
      <pre style="background: #222; padding: 10px; border-radius: 4px; overflow: auto; margin-top: 10px; font-size: 12px;">${error instanceof Error ? error.message : String(error)}</pre>
    </div>
  `;
}
